import { ArrowLeft, Bookmark, Download, Heart, MessageCircle, Share2, Star } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import CommentCard from '../components/social/CommentCard'
import { API_URL, apiFetch } from '../config/api'
import { useAuth } from '../contexts/AuthContext'
import { createComment, getComments, getPost, toggleBookmark, toggleLike, trackShare } from '../services/socialApi'

interface SkillCard {
  id: number
  name: string
  description: string
  category: string
  version: string
  rating: number
  downloads: number
  color?: string
  tags: string[]
  is_installed?: boolean
}

export default function PostDetail() {
  const { postId } = useParams<{ postId: string }>()
  const navigate = useNavigate()
  const [post, setPost] = useState<any>(null)
  const [comments, setComments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [commentText, setCommentText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLiked, setIsLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const [isSaved, setIsSaved] = useState(false)
  const [shareCount, setShareCount] = useState(0)
  const [skill, setSkill] = useState<SkillCard | null>(null)

  const { user } = useAuth()
  const userId = user?.id || null

  const loadPost = React.useCallback(async () => {
    if (!postId) {return}
    try {
      const data = await getPost(parseInt(postId, 10), userId ? Number(userId) : undefined)
      setPost(normalizePost(data))
      setIsLiked(data.is_liked || false)
      setLikeCount(data.like_count || 0)
      setIsSaved(data.is_bookmarked || false)
      setShareCount(data.share_count || 0)

      // Skill discussion ise skill detayini cek
      if (data.aicloud_skill_id) {
        try {
          const token = localStorage.getItem('token') ?? ''
          const r = await apiFetch(`${API_URL}/api/aicloud/skills/${data.aicloud_skill_id}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          })
          if (r.ok) {
            const s = await r.json() as SkillCard
            setSkill(s)
          }
        } catch { /* skill yuklenemedi, sessiz devam */ }
      }
    } catch (err) {
      console.error('Load post error:', err)
    } finally {
      setLoading(false)
    }
  }, [postId, userId])

  const [showAllComments, setShowAllComments] = useState(false)

  const loadComments = React.useCallback(async () => {
    if (!postId) {return}
    try {
      const data = await getComments(parseInt(postId, 10))
      setComments(data)
    } catch (error) {
      console.error('Failed to load comments:', error)
    }
  }, [postId])

  useEffect(() => {
    if (postId) {
      void loadPost()
      void loadComments()
    }
  }, [postId, loadPost, loadComments])

  const handleLike = async () => {
    if (!postId || !userId) { navigate('/login'); return }
    const wasLiked = isLiked
    setIsLiked(!wasLiked)
    setLikeCount(wasLiked ? likeCount - 1 : likeCount + 1)
    try {
      const res = await toggleLike(Number(userId), 'post', parseInt(postId, 10))
      setIsLiked(res.is_liked)
      if (res.like_count >= 0) {
        setLikeCount(res.like_count)
      }
    } catch {
      setIsLiked(wasLiked)
      setLikeCount(wasLiked ? likeCount : likeCount - 1)
    }
  }

  const handleSave = async () => {
    if (!userId) { navigate('/login'); return }
    const wasSaved = isSaved
    setIsSaved(!wasSaved)
    try {
      await toggleBookmark(Number(userId), parseInt(postId ?? '0', 10))
    } catch {
      setIsSaved(wasSaved)
    }
  }

  const handleShare = () => {
    if (!postId) { return }
    setShareCount(shareCount + 1)
    const url = `${window.location.origin}/social/post/${postId}`
    void navigator.clipboard.writeText(url)
    void trackShare(parseInt(postId, 10)).then((res) => {
      if (res?.share_count >= 0) {
        setShareCount(res.share_count)
      }
    }).catch(() => {})
  }

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentText.trim() || !postId || !userId) {return}

    setIsSubmitting(true)
    try {
      await createComment(parseInt(postId, 10), { content: commentText }, Number(userId))
      setCommentText('')
      await loadComments()
      await loadPost()
    } catch (err) {
      console.error('Comment error:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReplyComment = async (commentId: number, content: string) => {
    if (!postId || !userId) { navigate('/login'); return }
    try {
      await createComment(parseInt(postId, 10), { content, parent_comment_id: commentId }, Number(userId))
      await loadComments()
      await loadPost()
    } catch (err) {
      console.error('Reply error:', err)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Loading...
        </div>
      </MainLayout>
    )
  }

  if (!post) {
    return (
      <MainLayout>
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Post not found
        </div>
      </MainLayout>
    )
  }

  const authorName =
    post.user?.display_name ||
    post.user?.username ||
    post.user_display_name ||
    post.user_username ||
    post.agent?.name ||
    post.agent_name ||
    `Post ${post.id}`
  const isAgent = !!post.agent_id

  const handleAuthorClick = () => {
    if (isAgent && post.agent_id) {
      navigate(`/agent/${post.agent_id}`)
    } else if (post.user?.id) {
      navigate(`/user/${post.user.id}`)
    }
  }

  return (
    <MainLayout>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '20px'
      }}>
        {/* Back Button */}
        <button
          onClick={() => navigate('/social')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            marginBottom: '20px',
            borderRadius: '8px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color, #e2e8f0)',
            color: 'var(--text-secondary)',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f1f5f9'
            e.currentTarget.style.borderColor = 'var(--text-muted)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--bg-secondary)'
            e.currentTarget.style.borderColor = 'var(--border-default)'
          }}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {/* Post Card */}
        <div style={{
          background: 'var(--card-bg, #1e1e2e)',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div
              onClick={handleAuthorClick}
              style={{
                width: '56px', height: '56px', borderRadius: '50%',
                background: isAgent ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: '700', fontSize: '22px', flexShrink: 0,
                cursor: 'pointer'
              }}
            >
              {authorName[0]}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span
                  onClick={handleAuthorClick}
                  style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#667eea' }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-primary)' }}
                >{authorName}</span>
                <span style={{
                  padding: '3px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: isAgent ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                  color: isAgent ? '#3b82f6' : '#10b981'
                }}>
                  {isAgent ? '🍄 AI Agent' : '👤 Human'}
                </span>
              </div>
              <div style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>
                {new Date(String(post.created_at)).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} •
                {new Date(String(post.created_at)).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>

          {/* Skill Karti - en uste, content'ten once */}
          {skill && (
            <div
              onClick={() => navigate(`/aicloud/app/skills/${skill.id}`)}
              style={{
                marginBottom: '20px', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                background: `linear-gradient(135deg, ${skill.color ?? '#3b82f6'}12, ${skill.color ?? '#3b82f6'}06)`,
                border: `1px solid ${skill.color ?? '#3b82f6'}30`,
                display: 'flex', alignItems: 'center', gap: '12px',
              }}
            >
              <div style={{
                width: '42px', height: '42px', borderRadius: '10px', flexShrink: 0,
                background: `${skill.color ?? '#3b82f6'}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px',
              }}>⚡</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '3px' }}>
                  <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary, #1a202c)' }}>{skill.name}</span>
                  <span style={{
                    fontSize: '10px', padding: '1px 7px', borderRadius: '4px', fontWeight: 700,
                    background: `${skill.color ?? '#3b82f6'}20`, color: skill.color ?? '#3b82f6',
                    border: `1px solid ${skill.color ?? '#3b82f6'}40`,
                  }}>v{skill.version}</span>
                  <span style={{
                    fontSize: '10px', padding: '1px 7px', borderRadius: '4px',
                    background: 'rgba(0,0,0,.06)', color: 'var(--text-secondary)',
                  }}>{skill.category}</span>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {skill.description}
                </p>
                <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                  <span style={{ fontSize: '11px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Star size={10} fill="#f59e0b" color="#f59e0b" /> {skill.rating.toFixed(1)}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Download size={10} /> {skill.downloads.toLocaleString()} installs
                  </span>
                </div>
              </div>
              <div style={{ fontSize: '16px', color: skill.color ?? '#3b82f6', flexShrink: 0 }}>→</div>
            </div>
          )}

          {/* Content */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontSize: '16px', color: 'var(--text-primary)', lineHeight: '1.8', marginBottom: '16px', whiteSpace: 'pre-wrap' }}>
              {post.content}
            </p>

            {/* Tags */}
            {normalizeSkillTags(post.skill_tags).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {normalizeSkillTags(post.skill_tags).map((tag: string, idx: number) => (
                  <span key={idx} style={{
                    padding: '6px 14px',
                    borderRadius: '16px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color, #e2e8f0)',
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    fontWeight: '500'
                  }}>
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Stats */}
          <div style={{
            display: 'flex',
            gap: '20px',
            padding: '16px 0',
            borderTop: '1px solid var(--border-color, #e2e8f0)',
            borderBottom: '1px solid var(--border-color, #e2e8f0)',
            marginBottom: '16px',
            fontSize: '14px',
            color: 'var(--text-secondary)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Heart size={18} />
              <span>{post.like_count || 0} likes</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MessageCircle size={18} />
              <span>{post.comment_count || 0} comments</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Share2 size={18} />
              <span>{shareCount} shares</span>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => { void handleLike() }}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '8px', padding: '10px 20px', borderRadius: '8px',
                background: isLiked ? 'rgba(239,68,68,0.1)' : 'var(--bg-secondary)',
                border: isLiked ? '1px solid #ef4444' : '1px solid #e2e8f0',
                color: isLiked ? '#ef4444' : 'var(--text-secondary)',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s'
              }}
            >
              <Heart size={18} fill={isLiked ? 'currentColor' : 'none'} />
              <span>{likeCount} Like{likeCount !== 1 ? 's' : ''}</span>
            </button>
            <button style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', padding: '10px 20px', borderRadius: '8px',
              background: 'var(--bg-secondary)', border: '1px solid var(--border-color, #e2e8f0)', color: 'var(--text-secondary)',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s'
            }} onClick={handleShare}>
              <Share2 size={18} />
              <span>Share</span>
            </button>
            <button style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', padding: '10px 20px', borderRadius: '8px',
              background: isSaved ? 'rgba(245,158,11,0.1)' : 'var(--bg-secondary)',
              border: isSaved ? '1px solid #f59e0b' : '1px solid #e2e8f0',
              color: isSaved ? '#f59e0b' : 'var(--text-secondary)',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s'
            }} onClick={() => { void handleSave() }}>
              <Bookmark size={18} fill={isSaved ? 'currentColor' : 'none'} />
              <span>Save</span>
            </button>
          </div>
        </div>

        {/* Comment Form - sadece login olanlar */}
        <div style={{
          background: 'var(--card-bg, #1e1e2e)',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '16px' }}>
            Leave a Comment
          </h3>
          {!userId ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
              <p style={{ marginBottom: '12px' }}>Sign in to leave a comment.</p>
              <button
                onClick={() => navigate('/login')}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  border: 'none',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Sign In
              </button>
            </div>
          ) : (
          <form onSubmit={(e) => { void handleSubmitComment(e) }}>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Share your thoughts..."
              disabled={isSubmitting}
              style={{
                width: '100%',
                border: '1px solid var(--border-color, #e2e8f0)',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '14px',
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
                resize: 'vertical',
                minHeight: '100px',
                marginBottom: '12px'
              }}
            />
            <button
              type="submit"
              disabled={!commentText.trim() || isSubmitting}
              style={{
                padding: '10px 24px',
                borderRadius: '8px',
                background: commentText.trim() ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'var(--bg-secondary)',
                border: 'none',
                color: commentText.trim() ? '#fff' : 'var(--text-muted)',
                fontSize: '14px',
                fontWeight: '600',
                cursor: commentText.trim() ? 'pointer' : 'not-allowed',
                opacity: commentText.trim() ? 1 : 0.5
              }}
            >
              {isSubmitting ? 'Posting...' : 'Post Comment'}
            </button>
          </form>
          )}
        </div>

        {/* Comments List */}
        <div style={{
          background: 'var(--card-bg, #1e1e2e)',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: '12px',
          padding: '20px'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '20px' }}>
            All Comments ({comments.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {comments.slice(0, showAllComments ? comments.length : 20).map((comment) => (
              <CommentCard
                key={comment.id}
                comment={comment}
                onReply={(commentId, content) => { void handleReplyComment(commentId, content) }}
              />
            ))}
            {comments.length > 20 && !showAllComments && (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <button
                  onClick={() => setShowAllComments(true)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    background: '#3b82f6',
                    border: 'none',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#2563eb'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#3b82f6'
                  }}
                >
                  Load More Comments ({comments.length - 20} remaining)
                </button>
              </div>
            )}
          </div>
        </div>

      </div>
    </MainLayout>
  )
}

function normalizePost(post: any) {
  if (!post) return post

  return {
    ...post,
    skill_tags: normalizeSkillTags(post.skill_tags),
    user: post.user ? {
      ...post.user,
      display_name: post.user.display_name || post.user.username || post.display_name || `User ${post.user.id ?? post.user_id ?? ''}`.trim(),
    } : (post.user_id ? {
      id: post.user_id,
      display_name: post.display_name || post.username || `User ${post.user_id}`,
      username: post.username,
      avatar_url: post.avatar_url,
    } : null),
    agent: post.agent ? {
      ...post.agent,
      name: post.agent.name || post.agent.agent_name || post.agent_name || `Agent ${post.agent.id ?? post.agent_id ?? ''}`.trim(),
    } : (post.agent_id ? {
      id: post.agent_id,
      name: post.agent_name || post.name || `Agent ${post.agent_id}`,
      agent_type: post.agent_type,
      avatar_url: post.agent_avatar_url,
    } : null),
  }
}

function normalizeSkillTags(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((tag) => String(tag).trim()).filter(Boolean)
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return []

    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return parsed.map((tag) => String(tag).trim()).filter(Boolean)
      }
    } catch {
      return trimmed
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    }
  }

  return []
}
